import React from "react";
import { find, reduce } from "lodash";
import styled from "styled-components";
import DateFromNow from "@backpacker/date/from-now";
import { localize } from "@backpacker/number";
import { FontWeight, ellipsis } from "@glossy";
import {
  useCurrentUserLocale,
  useCurrentUserTimezone,
} from "@linkfluence/core/user/use-cases";
import { Facet } from "@linkfluence/models/facet";
import { Locale } from "@linkfluence/models/locale";
import Icon from "@ui-kit/icon";
import ClusterComponent from "@radarly/components/image-clustering/cluster";
import { useFacetsFromContext } from "@radarly/core/facets/use-cases";
import { tr } from "@radarly/i18n";
import { Cluster } from "@radarly/models/insights";
import { Project } from "@radarly/models/project";

const TooltipHeader = styled.div`
  margin-bottom: 6px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
`;

const TooltipAuthor = styled.div`
  ${ellipsis()};
  max-width: 110px;
  margin-right: 5px;
`;

const StyledIcon = styled(Icon)`
  margin-right: 5px;
`;

const TooltipMetric = styled.div`
  font-weight: ${FontWeight.Normal};
  text-align: left;
  font-size: 12px;
`;

interface IProps {
  cluster: Cluster;
  onClickPreview?: () => void;
  display?: "row" | "grid";
  project: Project;
}

const getGauge = (facets: any, facet: Facet, values: Cluster["aggs"]) => {
  const facetData = facets[facet];
  const clusterData = values[facet].values;
  return reduce(
    facetData.values,
    (acc, value) => {
      const data = find(clusterData, { key: value.id });
      if (data !== undefined) {
        acc.push({
          label: value.label,
          color: value.color,
          amount: data.counts.doc,
        });
      }
      return acc;
    },
    []
  );
};

const tooltipScore = (score: number, locale: Locale): string =>
  score ? localize(locale, score) : tr(locale, "DATA_UNKNOWN::LABEL");

const SharedImageCluster = ({
  cluster,
  onClickPreview,
  display = "row",
  project,
}: IProps) => {
  const facets = useFacetsFromContext();
  const locale = useCurrentUserLocale();
  const timezone = useCurrentUserTimezone();

  return (
    <ClusterComponent
      postCount={cluster.total}
      tags={cluster.keywords}
      display={display}
      sentiments={getGauge(facets, Facet.Tones, cluster.aggs)}
      platforms={getGauge(facets, Facet.Platforms, cluster.aggs)}
      pictures={cluster.hits}
      locale={locale}
      onClickMore={onClickPreview && onClickPreview}
      renderTooltip={
        display === "row"
          ? null
          : (post) => (
              <>
                <TooltipHeader>
                  <StyledIcon glyph={post.platform} />
                  <TooltipAuthor>{post.author}</TooltipAuthor> -{" "}
                  <DateFromNow
                    date={post.date}
                    locale={locale}
                    timezone={timezone}
                  />
                </TooltipHeader>
                <TooltipMetric>
                  {tr(locale, `POST::METRIC::ENGAGEMENT`)}
                  {tr(locale, "PUNCTUATION::COLON")}{" "}
                  {tooltipScore(post.engagementScore, locale)}
                </TooltipMetric>
                <TooltipMetric>
                  {tr(locale, `POST::METRIC::REACH`)}
                  {tr(locale, "PUNCTUATION::COLON")}{" "}
                  {tooltipScore(post.reach, locale)}
                </TooltipMetric>
              </>
            )
      }
    />
  );
};

export default SharedImageCluster;
